import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Monitor, Smartphone, CheckCircle, Download, Laptop, FileCode2 } from "lucide-react";
import api from "@/lib/api";

export default function InstallGuideDialog({ open, onClose, onDirectInstall, canDirectInstall }) {
  const [tab, setTab] = useState("windows");

  const downloadWindowsInstaller = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/download/windows-setup`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="install-guide-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Download className="h-5 w-5 text-cyan-600" /> Sootrik PC Setup
          </DialogTitle>
          <DialogDescription>
            Two easy ways to install the software on Windows PC and Mobile:
          </DialogDescription>
        </DialogHeader>

        {/* 1-Click Windows .bat Installer Download */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl p-4 shadow-sm space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-bold text-sm">Windows 1-Click Setup File (.bat)</p>
              <p className="text-xs text-cyan-100 mt-0.5">
                Download this file and double-click it — a Sootrik icon will appear on your Desktop!
              </p>
            </div>
            <Laptop className="h-7 w-7 text-white/90 flex-shrink-0" />
          </div>
          <Button
            onClick={downloadWindowsInstaller}
            className="w-full bg-white text-slate-900 hover:bg-slate-100 font-bold gap-2 text-sm shadow"
            data-testid="download-windows-bat-btn"
          >
            <Download className="h-4 w-4 text-cyan-600" /> Download Windows Setup (.bat)
          </Button>
        </div>

        {canDirectInstall && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-cyan-900">Direct Browser Install (PWA)</p>
              <p className="text-[11px] text-cyan-700">Install directly without downloading a file</p>
            </div>
            <Button
              size="sm"
              onClick={() => { onDirectInstall(); onClose(); }}
              className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1 text-xs"
              data-testid="direct-install-action-btn"
            >
              <Download className="h-3.5 w-3.5" /> Install App
            </Button>
          </div>
        )}

        <div className="flex gap-2 border-b border-border pb-2">
          <button
            onClick={() => setTab("windows")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
              tab === "windows" ? "bg-slate-900 text-white" : "text-muted-foreground hover:bg-slate-100"
            }`}
          >
            <Laptop className="h-3.5 w-3.5" /> Windows Setup Guide
          </button>
          <button
            onClick={() => setTab("mobile")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
              tab === "mobile" ? "bg-slate-900 text-white" : "text-muted-foreground hover:bg-slate-100"
            }`}
          >
            <Smartphone className="h-3.5 w-3.5" /> Mobile Guide
          </button>
        </div>

        {tab === "windows" ? (
          <div className="space-y-2.5 text-xs text-slate-600">
            <p className="font-semibold text-slate-800 text-sm">How to use the Windows Installer file:</p>
            <div className="space-y-1.5">
              <div className="flex gap-2 items-start">
                <span className="w-4 h-4 rounded-full bg-cyan-100 text-cyan-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <p>Click the <b>"Download Windows Setup (.bat)"</b> button above.</p>
              </div>
              <div className="flex gap-2 items-start">
                <span className="w-4 h-4 rounded-full bg-cyan-100 text-cyan-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <p>Double-click the <b>Sootrik-Setup.bat</b> file in the Downloads folder.</p>
              </div>
              <div className="flex gap-2 items-start">
                <span className="w-4 h-4 rounded-full bg-cyan-100 text-cyan-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <p>A <b>Sootrik icon will appear on your Desktop</b> — the software opens with one click from there.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5 text-xs text-slate-600">
            <p className="font-semibold text-slate-800 text-sm">In the Chrome browser on mobile:</p>
            <div className="space-y-1.5">
              <div className="flex gap-2 items-start">
                <span className="w-4 h-4 rounded-full bg-pink-100 text-pink-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <p>Open the link in mobile Chrome and tap the <b>3-dots (⋮) menu</b> at the top right.</p>
              </div>
              <div className="flex gap-2 items-start">
                <span className="w-4 h-4 rounded-full bg-pink-100 text-pink-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <p>Tap <b>"Install app"</b> / <b>"Add to Home screen"</b>.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

